'use client';

import { useSubscriptionQuery } from '@wwe-portal/web/providers/queries/subscription';
import {
  useIsAuthenticated,
  useIsAuthenticating,
} from '@wwe-portal/web/providers/stores/auth-store';
import { useTranslations } from 'next-intl';
import { HelloWorldCard } from '@wwe-portal/ui/features/club-store/milestone/hello-world-card';
import { useSubscriptionHistory } from '@wwe-portal/web/utils/club-store/helper';
import { TextSkeleton } from '@wwe-portal/ui/components/skeleton';
import { FC } from 'react';
import {
  CurrentSubscriptionPlan,
  SubscriptionBenefitType,
} from '@wwe-portal/entities/subscription.type';
import { useResponsive } from '@wwe-portal/ui/hooks/use-responsive';

const WelcomeSectionSkeleton = () => {
  return (
    <HelloWorldCard
      description={<TextSkeleton fontSize={14} lineHeight={18} />}
      buttonContent={
        <TextSkeleton fontSize={14} lineHeight={18} className="w-20" />
      }
    />
  );
};

export const WelcomeSectionContent: FC<{
  currentPlan: CurrentSubscriptionPlan | undefined;
  abilities: Record<SubscriptionBenefitType, boolean>;
}> = ({ currentPlan, abilities }) => {
  const isAuthenticated = useIsAuthenticated();
  const t = useTranslations();
  const { goToSubscription } = useSubscriptionHistory();
  const getDescription = () => {
    if (!isAuthenticated || currentPlan == null) {
      return t('subscription_introduction');
    }
    if (!abilities?.clubStoreAccess) {
      return t('subscription_lower_introduction');
    }

    return t('subscription_highest_introduction');
  };

  const getButtonContent = () => {
    if (!isAuthenticated || currentPlan == null) {
      return 'SUBSCRIBE NOW';
    }
    if (!abilities?.clubStoreAccess) {
      return 'UPGRADE NOW';
    }

    return undefined;
  };

  return (
    <HelloWorldCard
      onClick={goToSubscription}
      description={getDescription()}
      buttonContent={getButtonContent()}
    />
  );
};

export const WelcomeSection = () => {
  const { isHydrated } = useResponsive();
  const { currentPlan, abilities, isLoading } = useSubscriptionQuery();
  const isAuthenticatedLoading = useIsAuthenticating();

  if (isAuthenticatedLoading || isLoading || !isHydrated) {
    return <WelcomeSectionSkeleton />;
  }

  return (
    <WelcomeSectionContent currentPlan={currentPlan} abilities={abilities} />
  );
};
